import React, { useState, useEffect } from "react";
import Autosizer from "react-virtualized-auto-sizer";
import { FixedSizeList } from "react-window";
import { TreeView } from "./TreeView.js";
import { TreeViewItemExpandableOption } from "./TreeViewItemExpandableOption.js";
function VirtualizedExpandableTreeView({
  options,
  className,
  onItemClick,
  selectedValue,
  itemSize = 44
}) {
  const [flatOptions, setFlatOptions] = useState([]);
  const [expandedIds, setExpandedIds] = useState(/* @__PURE__ */ new Set());
  const updatedFlatOptions = mapToFlatOptions(options);
  const optionsIds = updatedFlatOptions?.map((o) => o.value);
  useEffect(() => {
    setFlatOptions(updatedFlatOptions);
    setExpandedIds(new Set(optionsIds));
  }, [optionsIds?.join()]);
  function mapToFlatOptions(options2, depth = 0, parentId = null) {
    const result = [];
    options2?.forEach((option, index) => {
      option.depth = depth;
      option.parentId = parentId;
      option.isLastChild = options2?.length - 1 === index;
      result.push(option, ...mapToFlatOptions(option.subOptions, depth + 1, option.value));
    });
    return result;
  }
  function handleExpand(e, itemId) {
    e.stopPropagation();
    let newList;
    const index = flatOptions.findIndex((i) => i.value === itemId);
    if (expandedIds.has(itemId)) {
      removeIdFromExpanded(itemId);
      newList = getListAfterCollapse(flatOptions, index);
    } else {
      addIdFromExpanded(itemId);
      newList = getListAfterExpand(flatOptions, index);
    }
    setFlatOptions(newList);
  }
  function getListAfterCollapse(list, collapsedIndex) {
    const result = [...list];
    const nextVisibleIndexAfterCollapsed = list.slice(collapsedIndex + 1).findIndex((i) => i.depth <= list[collapsedIndex].depth);
    const collapsedItemsCount = nextVisibleIndexAfterCollapsed > -1 ? list.slice(collapsedIndex + 1, nextVisibleIndexAfterCollapsed + collapsedIndex + 1)?.length : list.slice(collapsedIndex + 1).length;
    result.splice(collapsedIndex + 1, collapsedItemsCount);
    return result;
  }
  function getListAfterExpand(list, expandedIndex) {
    const start = list.slice(0, expandedIndex + 1);
    const mid = getSubExpandedItems(flatOptions[expandedIndex].subOptions);
    const end = flatOptions.slice(expandedIndex + 1);
    function getSubExpandedItems(options2) {
      const result = [];
      options2?.forEach((option) => {
        result.push(option, ...expandedIds.has(option.value) ? getSubExpandedItems(option.subOptions) : []);
      });
      return result;
    }
    return [start, mid, end].flat();
  }
  function removeIdFromExpanded(id) {
    setExpandedIds((prevState) => {
      prevState.delete(id);
      return new Set(prevState);
    });
  }
  function addIdFromExpanded(id) {
    setExpandedIds((prevState) => {
      prevState.add(id);
      return new Set(prevState);
    });
  }
  const optionsStorage = {};
  flatOptions?.forEach((option) => {
    optionsStorage[option.value] = option;
  });
  function getExistenceOfParentDepthLines(item) {
    const parent = optionsStorage[item.parentId];
    return parent ? [...getExistenceOfParentDepthLines(parent), !parent?.isLastChild] : [];
  }
  return /* @__PURE__ */ React.createElement(Autosizer, null, ({ height, width }) => /* @__PURE__ */ React.createElement(TreeView, null, /* @__PURE__ */ React.createElement(FixedSizeList, { height, width, itemSize, itemCount: flatOptions?.length, className }, ({ index, style }) => {
    const item = flatOptions[index];
    return /* @__PURE__ */ React.createElement(
      TreeViewItemExpandableOption,
      {
        key: item.value,
        label: item.label,
        depth: item.depth,
        itemId: item.value,
        onClick: onItemClick,
        onExpand: (e) => handleExpand(e, item.value),
        style,
        parentsDepthLines: getExistenceOfParentDepthLines(item),
        isLastChild: item.isLastChild,
        isExpanded: expandedIds?.has(item.value),
        isSelected: selectedValue === item.value,
        hasSubOptions: !!item?.subOptions?.length
      }
    );
  })));
}
export {
  VirtualizedExpandableTreeView
};
//# sourceMappingURL=VirtualizedExpandableTreeView.js.map
